'use client'

import Link from 'next/link'
import React from 'react'

import { containerClassName } from '@/components/Container'
import { PolaroidGalleryCarousel, type PolaroidGalleryItem } from '@/components/PolaroidGalleryCarousel'
import { Button } from '@/components/ui/button'
import { ColorTrailText } from '@/components/ui/color-trail-text'
import { localizePath } from '@/lib/i18n/config'
import { useLocale } from '@/lib/i18n/useLocale'
import { useMessages } from '@/lib/i18n/useMessages'
import type { LocationPolaroidGalleryConfig } from '@/lib/mock/locationPages'
import { cn } from '@/utilities/ui'

const BIRD_IMAGE = '/media/cities/berlin/bird.svg'

type LocationPolaroidGallerySectionProps = {
  gallery: LocationPolaroidGalleryConfig
  items: PolaroidGalleryItem[]
}

export function LocationPolaroidGallerySection({
  gallery,
  items,
}: LocationPolaroidGallerySectionProps) {
  const locale = useLocale()
  const copy = useMessages().location.polaroidGallery
  const birdSitsBesideTitle = locale === 'de' || locale === 'zh'
  const titleSegments = [
    {
      finalColor: 'var(--brand-bluetext)',
      text: copy.titleBeforeHighlight,
      trail: false,
    },
    {
      ...(locale === 'de' ? { className: 'block' } : {}),
      finalColor: 'var(--brand-dark-green)',
      text: copy.titleHighlight,
    },
  ]
  const ctaHref =
    gallery.ctaHref && gallery.ctaHref.startsWith('/')
      ? localizePath(gallery.ctaHref, locale)
      : gallery.ctaHref

  return (
    <section
      aria-labelledby="location-polaroid-gallery-heading"
      className="bg-cream"
    >
      <div className={cn(containerClassName, 'relative z-20 pb-0 pt-12 lg:pb-0 lg:pt-20')}>
        <div className="flex justify-center px-4">
          {birdSitsBesideTitle ? (
            <div className="flex max-w-5xl flex-col items-center gap-4 lg:flex-row lg:items-center lg:gap-8">
              <ColorTrailText
                as="h2"
                className="text-balance text-center text-title text-bluetext lg:text-center"
                id="location-polaroid-gallery-heading"
                segments={titleSegments}
              />
              <img
                alt=""
                aria-hidden
                className="pointer-events-none hidden h-[10rem] w-[10rem] shrink-0 lg:block lg:h-[217px] lg:w-[217px]"
                src={BIRD_IMAGE}
              />
            </div>
          ) : (
            <div className="relative inline-block max-w-4xl">
              <ColorTrailText
                as="h2"
                className="text-balance text-center text-title text-bluetext"
                id="location-polaroid-gallery-heading"
                segments={titleSegments}
              />
              <img
                alt=""
                aria-hidden
                className="pointer-events-none absolute -right-10 -top-1 hidden h-[217px] w-[217px] lg:block lg:-right-14 xl:-right-16"
                src={BIRD_IMAGE}
              />
            </div>
          )}
        </div>
      </div>

      <PolaroidGalleryCarousel
        ariaLabel={gallery.ariaLabel}
        autoplayPauseLabel={gallery.autoplayPauseLabel}
        autoplayPlayLabel={gallery.autoplayPlayLabel}
        className="relative z-10 pb-10 lg:pb-12"
        detailDialog={{
          closeLabel: gallery.detailCloseLabel,
        }}
        items={items}
        playLabel={gallery.playLabel}
        previousLabel={gallery.previousLabel}
        nextLabel={gallery.nextLabel}
      />

      {gallery.ctaLabel && ctaHref ? (
        <div className={cn(containerClassName, 'py-0 lg:py-0')}>
          <div className="flex justify-center">
            <Button
              asChild
              className="rounded-[10px] border-[1.5px] border-black/30 bg-white/30 font-outfit text-base font-medium text-dark-800/80"
              variant="outline"
            >
              <Link href={ctaHref}>{gallery.ctaLabel}</Link>
            </Button>
          </div>
        </div>
      ) : null}
    </section>
  )
}
